/**
 * Mobile layout of the chat-teaser section.
 *
 * The card stack (SOLO + AIS UNITED) fills the full height/width; the category
 * tabs move to a horizontal bar at the bottom.
 */
"use client";
import type { CSSProperties, ReactElement } from "react";
import { motion } from "framer-motion";
import { Chatteaser3SoloCard } from "./Chatteaser3SoloCard";
import { Chatteaser3UnitedCard, CHATTEASER3_UNITED_TAB_HEIGHT_MODE, } from "./Chatteaser3UnitedCard";
import { CHATTEASER3_ANIM_PHASE, CHATTEASER3_CONTENT_FADE_PRESET, } from "./chatteaser3Anim";
import { deriveIntro } from "./chatteaser3Intro";
import { PAGE_STYLE } from "./chatteaser3.styles";
import { getIntroRestHeight } from "./useChatteaser3ScrollMotion";
import { MOBILE_WRAPPER_STYLE } from "./chatteaser3.mobile.styles";
import { Chatteaser3MobileTabs } from "./chatteaser3MobileTabs";
import { useChatteaser3MobileRuntime } from "./useChatteaser3MobileRuntime";
type MobileCardCssVars = Record<`--${string}`, string>;
const MOBILE_CARD_EDGE_PADDING = 25;
const MOBILE_CARD_BOTTOM_PADDING = 170;
const MOBILE_CARD_CAROUSEL_SHIFT = 48;
const MOBILE_MESSAGE_FONT_SIZE = 17;
const MOBILE_MENTION_PILL_FONT_SIZE = 15;
const MOBILE_MENTION_PILL_ICON_SIZE = 20;
const MOBILE_CARD_ENTER_BLUR = "blur(3px)";
const MOBILE_CARD_EXIT_BLUR = "blur(2px)";
const MOBILE_CARD_SHARP_FILTER = "blur(0px)";
const MOBILE_CARD_IDLE_FILTER = "none";
const MOBILE_PAGE_STYLE: CSSProperties = {
    ...PAGE_STYLE,
    display: "flex",
    flexDirection: "column",
};
const MOBILE_CARD_STAGE_STYLE: CSSProperties = {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    minHeight: 0,
    width: "100%",
    boxSizing: "border-box",
    padding: `${MOBILE_CARD_EDGE_PADDING}px ${MOBILE_CARD_EDGE_PADDING}px ${MOBILE_CARD_BOTTOM_PADDING}px`,
};
const MOBILE_CARD_FRAME_STYLE: CSSProperties = {
    position: "relative",
    flex: "1 1 auto",
    minHeight: 0,
    width: "100%",
};
const MOBILE_CARD_CAROUSEL_LAYER_STYLE: CSSProperties = {
    position: "absolute",
    inset: 0,
};
const MOBILE_CARD_CAROUSEL_EXIT_TRANSITION = {
    type: "spring",
    duration: 0.18,
    bounce: 0,
} as const;
const MOBILE_CARD_CAROUSEL_ENTER_TRANSITION = {
    type: "spring",
    duration: 0.38,
    bounce: 0,
} as const;
const MOBILE_CARD_CAROUSEL_REDUCED_TRANSITION = { duration: 0 } as const;
const MOBILE_SCROLL_PROBE_CLASS_NAME = "chatteaser3-mobile-scroll-probe";
enum MobileScrollTouchAction {
    PanY = "pan-y"
}
const MOBILE_SCROLL_PROBE_STYLE: CSSProperties = {
    position: "absolute",
    inset: 0,
    overflowY: "auto",
    overflowX: "hidden",
    zIndex: 10,
    touchAction: MobileScrollTouchAction.PanY,
};
interface ChatTeaser3MobileExperienceProps {
    fillParent?: boolean;
}
export function ChatTeaser3MobileExperience({ fillParent = false, }: ChatTeaser3MobileExperienceProps): ReactElement {
    const { activeTab, animPhase, carouselDirection, displayedTab, handleSelectTab, intro, layout, layoutVersion, messagesInnerRef, onWheel, scrollContainerRef, scrollMotion, scrollProgress, shouldReduceMotion, soloContentRef, soloRef, spread, swipeHandlers, tabContent, tabVariant, tabs, unitedHeaderRef, unitedScrollerRef, wrapperRef, } = useChatteaser3MobileRuntime();
    const { solo, united } = tabContent;
    const introValues = deriveIntro(intro.introProgress, {
        wrapperHeight: layout.wrapperHeight,
        restHeight: getIntroRestHeight(layout),
    });
    const isCarouselExit = !shouldReduceMotion && animPhase === CHATTEASER3_ANIM_PHASE.Exit;
    const isCarouselEnter = !shouldReduceMotion && animPhase === CHATTEASER3_ANIM_PHASE.Enter;
    const isCarouselAnimating = isCarouselExit || isCarouselEnter;
    const carouselShiftX = MOBILE_CARD_CAROUSEL_SHIFT * carouselDirection;
    const cardCarouselInitial = isCarouselEnter
        ? { opacity: 0, x: carouselShiftX, filter: MOBILE_CARD_ENTER_BLUR }
        : false;
    const cardCarouselTransition = shouldReduceMotion
        ? MOBILE_CARD_CAROUSEL_REDUCED_TRANSITION
        : isCarouselExit
            ? MOBILE_CARD_CAROUSEL_EXIT_TRANSITION
            : isCarouselEnter
                ? MOBILE_CARD_CAROUSEL_ENTER_TRANSITION
                : MOBILE_CARD_CAROUSEL_REDUCED_TRANSITION;
    const cardCarouselLayerStyle: CSSProperties = {
        ...MOBILE_CARD_CAROUSEL_LAYER_STYLE,
        willChange: isCarouselAnimating ? "transform, opacity, filter" : "auto",
    };
    const mobileWrapperStyle = {
        ...MOBILE_WRAPPER_STYLE,
        "--ct3-message-font-size": `${MOBILE_MESSAGE_FONT_SIZE}px`,
        "--ct3-mention-pill-font-size": `${MOBILE_MENTION_PILL_FONT_SIZE}px`,
        "--ct3-mention-pill-gap": "5px",
        "--ct3-mention-pill-margin-x": "1px",
        "--ct3-mention-pill-padding": "2px 9px 2px 3px",
        "--ct3-mention-pill-icon-size": `${MOBILE_MENTION_PILL_ICON_SIZE}px`,
        "--ct3-mention-pill-icon-padding": "1px",
    } satisfies CSSProperties & MobileCardCssVars;
    const mobilePageStyle: CSSProperties = {
        ...MOBILE_PAGE_STYLE,
        height: fillParent ? "100%" : MOBILE_PAGE_STYLE.height,
        minHeight: fillParent ? "100%" : undefined,
    };
    return (
      <section data-el="Cchatteaser3/MobileExperience/Root" style={mobilePageStyle}>
        <div style={MOBILE_CARD_STAGE_STYLE}>
          <div style={MOBILE_CARD_FRAME_STYLE}>
            <div ref={wrapperRef} style={mobileWrapperStyle}>
              <motion.div data-el="Cchatteaser3/MobileExperience/Carousel" key={displayedTab} style={cardCarouselLayerStyle} initial={cardCarouselInitial} animate={{
            opacity: isCarouselExit ? 0 : 1,
            x: isCarouselExit ? -carouselShiftX : 0,
            filter: isCarouselExit
                ? MOBILE_CARD_EXIT_BLUR
                : isCarouselEnter
                    ? MOBILE_CARD_SHARP_FILTER
                    : MOBILE_CARD_IDLE_FILTER,
        }} transition={cardCarouselTransition}>
                <Chatteaser3SoloCard animPhase={animPhase} contentFadePreset={CHATTEASER3_CONTENT_FADE_PRESET.Mobile} displayedTab={displayedTab} introActive={introValues.active} introSoloOpacity={introValues.soloOpacity} solo={solo} soloContentRef={soloContentRef} soloRef={soloRef} soloScrollOpacity={scrollMotion.soloScrollOpacity}/>

                <Chatteaser3UnitedCard animPhase={animPhase} contentFadePreset={CHATTEASER3_CONTENT_FADE_PRESET.Mobile} displayedTab={displayedTab} introActive={introValues.active} introUnitedHeight={introValues.unitedHeight} introUnitedOpacity={introValues.unitedOpacity} introMessagesOpacity={introValues.messagesOpacity} introMessagesRise={introValues.messagesRise} layout={layout} layoutVersion={layoutVersion} messageScroll={scrollMotion.messageScroll} messagesInnerRef={messagesInnerRef} restHeight={scrollMotion.restHeight} tabHeightMode={CHATTEASER3_UNITED_TAB_HEIGHT_MODE.CollapseOnExit} united={united} unitedHeaderRef={unitedHeaderRef} unitedScrollerRef={unitedScrollerRef}/>
              </motion.div>
            </div>

            <div data-el="Cchatteaser3/MobileExperience/ScrollViewport" ref={scrollContainerRef} className={MOBILE_SCROLL_PROBE_CLASS_NAME} style={MOBILE_SCROLL_PROBE_STYLE} onWheel={onWheel} {...swipeHandlers}>
              <div style={{ height: layout.wrapperHeight + layout.maxScroll }}/>
            </div>
          </div>
        </div>

        <Chatteaser3MobileTabs variant={tabVariant} tabs={tabs} activeTab={activeTab} onSelect={handleSelectTab} spread={spread} reveal={introValues.railReel} scrollProgress={scrollProgress} onWheel={onWheel} swipeHandlers={swipeHandlers}/>
      </section>
    );
}
